import AlbumIcon from "@mui/icons-material/Album";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import MenuIcon from "@mui/icons-material/Menu";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import PersonIcon from "@mui/icons-material/Person";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import QueueMusicIcon from "@mui/icons-material/QueueMusic";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import { Box, Typography } from "@mui/material";
import { Command } from "cmdk";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePlaylist } from "../../contexts/PlaylistContext";
import { shouldIgnoreGlobalShortcut } from "@/utils/shortcutGuard";
import { getArtistPath, trackClick } from "../_helpers";

interface CommandAction {
  id: string;
  name: string;
  shortcut?: string[];
  keywords: string[];
  section: string;
  perform: () => void;
  icon?: React.ReactNode;
  subtitle?: string;
}

const createBaseActions = (
  navigate: ReturnType<typeof useNavigate>,
  togglePlayPause: () => void,
  togglePanel: () => void,
  isPlaying: boolean,
  currentTrack: any
): CommandAction[] => [
  {
    id: "homeAction",
    name: "Home",
    shortcut: ["H"],
    keywords: ["home", "main", "start"],
    section: "Navigation",
    perform: () => navigate(getArtistPath("truevined")),
    icon: <HomeIcon sx={{ fontSize: 18 }} />,
    subtitle: "Go to homepage",
  },
  {
    id: "musicAction",
    name: "Music",
    shortcut: ["M"],
    keywords: ["music", "songs", "tracks", "releases"],
    section: "Navigation",
    perform: () => navigate(getArtistPath("truevined", "/songs")),
    icon: <MusicNoteIcon sx={{ fontSize: 18 }} />,
    subtitle: "Browse all songs",
  },
  {
    id: "albumsAction",
    name: "Albums",
    shortcut: ["A"],
    keywords: ["albums", "discography", "records"],
    section: "Navigation",
    perform: () => navigate(getArtistPath("truevined", "/albums")),
    icon: <AlbumIcon sx={{ fontSize: 18 }} />,
    subtitle: "Browse all albums",
  },
  {
    id: "videosAction",
    name: "Videos",
    shortcut: ["V"],
    keywords: ["videos", "youtube", "watch"],
    section: "Navigation",
    perform: () => navigate(getArtistPath("truevined", "/videos")),
    icon: <VideoLibraryIcon sx={{ fontSize: 18 }} />,
    subtitle: "Watch music videos",
  },
  {
    id: "aboutAction",
    name: "About",
    shortcut: ["B"],
    keywords: ["about", "bio", "biography", "story"],
    section: "Navigation",
    perform: () => navigate(getArtistPath("truevined", "/bio")),
    icon: <InfoIcon sx={{ fontSize: 18 }} />,
    subtitle: "Learn about the artist",
  },
  {
    id: "scrollToMusicAction",
    name: "Go to Music Section",
    keywords: ["scroll", "music", "section"],
    section: "Sections",
    perform: () => {
      const section = document.querySelector(".music-section");
      if (section) {
        const offset = 100;
        const elementPosition = section.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      }
    },
    icon: <MusicNoteIcon sx={{ fontSize: 18 }} />,
    subtitle: "Scroll to music section",
  },
  {
    id: "scrollToAboutAction",
    name: "Go to About Section",
    keywords: ["scroll", "about", "bio", "section"],
    section: "Sections",
    perform: () => {
      const section = document.querySelector(".BioSection");
      if (section) {
        const offset = 100;
        const elementPosition = section.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      }
    },
    icon: <InfoIcon sx={{ fontSize: 18 }} />,
    subtitle: "Scroll to about section",
  },
  {
    id: "scrollToGalleryAction",
    name: "Go to Gallery Section",
    keywords: ["scroll", "gallery", "images", "photos", "section"],
    section: "Sections",
    perform: () => {
      const section = document.querySelector(".GallerySection");
      if (section) {
        const offset = 100;
        const elementPosition = section.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      }
    },
    icon: <PersonIcon sx={{ fontSize: 18 }} />,
    subtitle: "Scroll to gallery section",
  },
  {
    id: "scrollToContactAction",
    name: "Go to Contact Section",
    keywords: ["scroll", "contact", "email", "section"],
    section: "Sections",
    perform: () => {
      const section = document.querySelector(".ContactSection");
      if (section) {
        const offset = 100;
        const elementPosition = section.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      }
    },
    icon: <PersonIcon sx={{ fontSize: 18 }} />,
    subtitle: "Scroll to contact section",
  },
  {
    id: "togglePlayAction",
    name: currentTrack ? (isPlaying ? "Pause Music" : "Play Music") : "Play Music",
    shortcut: ["Space"],
    keywords: ["play", "pause", "music", "audio", "toggle"],
    section: "Player",
    perform: () => togglePlayPause(),
    icon: <PlayArrowIcon sx={{ fontSize: 18 }} />,
    subtitle: currentTrack ? `Currently: ${currentTrack.title || currentTrack.name}` : "Toggle music playback",
  },
  {
    id: "togglePlaylistAction",
    name: "Toggle Playlist Panel",
    shortcut: ["P"],
    keywords: ["playlist", "queue", "panel", "toggle"],
    section: "Player",
    perform: () => togglePanel(),
    icon: <QueueMusicIcon sx={{ fontSize: 18 }} />,
    subtitle: "Show/hide playlist panel",
  },
  {
    id: "toggleMusicPlayerAction",
    name: "Toggle Music Player",
    shortcut: ["Shift", "M"],
    keywords: ["player", "music", "toggle", "show", "hide"],
    section: "Player",
    perform: () => {
      const event = new CustomEvent("toggleMusicPlayer");
      document.dispatchEvent(event);
    },
    icon: <GraphicEqIcon sx={{ fontSize: 18 }} />,
    subtitle: "Show/hide music player",
  },
  {
    id: "toggleMenuAction",
    name: "Toggle Menu",
    shortcut: ["Esc"],
    keywords: ["menu", "navigation", "close"],
    section: "Interface",
    perform: () => {
      const event = new CustomEvent("toggleMenu");
      document.dispatchEvent(event);
    },
    icon: <MenuIcon sx={{ fontSize: 18 }} />,
    subtitle: "Open/close navigation menu",
  },
];

function CommandBar() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { togglePlayPause, togglePanel, isPlaying, currentTrack } = usePlaylist();

  const actions = createBaseActions(navigate, togglePlayPause, togglePanel, isPlaying, currentTrack);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
        return;
      }

      if (open || e.altKey || shouldIgnoreGlobalShortcut(e)) return;

      if (e.key === "/") {
        e.preventDefault();
        setOpen(true);
        return;
      }

      if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        togglePlayPause();
        return;
      }

      if (e.key === "p" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        togglePanel();
        return;
      }

      if (e.key === "m" && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
        e.preventDefault();
        navigate(getArtistPath("truevined", "/songs"));
        return;
      }

      if (e.key === "M" && e.shiftKey) {
        e.preventDefault();
        const event = new CustomEvent("toggleMusicPlayer");
        document.dispatchEvent(event);
        return;
      }

      if (e.key === "h" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        navigate(getArtistPath("truevined"));
        return;
      }

      if (e.key === "a" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        navigate(getArtistPath("truevined", "/albums"));
        return;
      }

      if (e.key === "v" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        navigate(getArtistPath("truevined", "/videos"));
        return;
      }

      if (e.key === "b" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        navigate(getArtistPath("truevined", "/bio"));
        return;
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, togglePlayPause, togglePanel, navigate]);

  const handleClose = () => {
    setOpen(false);
    setSearch("");
  };

  const groupedActions = actions.reduce<Record<string, CommandAction[]>>((groups, action) => {
    const section = action.section || "Other";
    if (!groups[section]) {
      groups[section] = [];
    }
    groups[section].push(action);
    return groups;
  }, {});

  const sectionOrder = ["Navigation", "Sections", "Player", "Interface"];
  const orderedSections = sectionOrder.filter((section) => groupedActions[section]);

  if (!open) {
    return null;
  }

  return (
    <>
      <Box
        onClick={handleClose}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.85)",
          backdropFilter: "blur(7px)",
          zIndex: 9998,
          animation: "commandBackdropIn 300ms cubic-bezier(0.16, 1, 0.3, 1)",
          "@keyframes commandBackdropIn": {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      />
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          padding: { xs: "15vh 1rem", md: "15vh 2rem" },
          zIndex: 9999,
          pointerEvents: "none",
        }}
      >
        <Command
          style={{
            maxWidth: "580px",
            width: "100%",
            background: "rgba(15, 15, 15, 0.98)",
            borderRadius: "16px",
            overflow: "hidden",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(16px)",
            pointerEvents: "auto",
            animation: "commandBarIn 400ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          <style>
            {`
              @keyframes commandBarIn {
                from {
                  opacity: 0;
                  transform: translateY(-8px) scale(0.96);
                }
                to {
                  opacity: 1;
                  transform: translateY(0) scale(1);
                }
              }
              [cmdk-input] {
                font-family: "Poppins", -apple-system, BlinkMacSystemFont, sans-serif;
                border: none;
                width: 100%;
                font-size: 18px;
                padding: 20px 24px;
                outline: none;
                background: transparent;
                color: #ffffff;
                border-bottom: 1px solid rgba(255, 255, 255, 0.08);
              }
              [cmdk-input]::placeholder {
                color: rgba(255, 255, 255, 0.4);
              }
              [cmdk-list] {
                max-height: 420px;
                overflow: auto;
                overscroll-behavior: contain;
                padding: 8px 0;
              }
              [cmdk-group-heading] {
                user-select: none;
                font-size: 11px;
                color: rgba(255, 255, 255, 0.5);
                padding: 16px 24px 8px 24px;
                display: flex;
                align-items: center;
                font-weight: 500;
                text-transform: uppercase;
                letter-spacing: 1px;
                font-family: "Poppins", sans-serif;
              }
              [cmdk-item] {
                cursor: pointer;
                min-height: 48px;
                font-size: 15px;
                display: flex;
                align-items: center;
                gap: 14px;
                padding: 12px 24px;
                color: rgba(255, 255, 255, 0.9);
                user-select: none;
                transition: all 150ms ease;
                font-family: "Poppins", sans-serif;
                border-left: 3px solid transparent;
              }
              [cmdk-item][aria-selected="true"] {
                background: rgba(var(--logoColor-rgb, 212, 165, 116), 0.12);
                color: var(--logoColor, #d4a574);
                border-left-color: var(--logoColor, #d4a574);
              }
              [cmdk-item]:active {
                transform: scale(0.98);
              }
              [cmdk-empty] {
                padding: 24px;
                text-align: center;
                color: rgba(255, 255, 255, 0.5);
                font-family: "Poppins", sans-serif;
              }
            `}
          </style>
          <Command.Input
            placeholder="Type a command or search..."
            autoFocus
            value={search}
            onValueChange={setSearch}
          />
          <Command.List>
            <Command.Empty>No results found.</Command.Empty>
            {orderedSections.map((section) => (
              <Command.Group key={section} heading={section}>
                {groupedActions[section].map((action) => (
                  <Command.Item
                    key={action.id}
                    value={`${action.name} ${action.keywords.join(" ")}`}
                    onSelect={() => {
                      trackClick(`command-bar:${action.id}`, { label: action.name, section: "command-bar" });
                      action.perform();
                      handleClose();
                    }}
                  >
                    {action.icon && (
                      <Box sx={{ color: "var(--logoColor, #d4a574)", display: "flex", alignItems: "center" }}>
                        {action.icon}
                      </Box>
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: "15px", fontWeight: 500 }}>{action.name}</Typography>
                      {action.subtitle && (
                        <Typography sx={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.4)", mt: "2px" }}>
                          {action.subtitle}
                        </Typography>
                      )}
                    </Box>
                    {action.shortcut && action.shortcut.length > 0 && (
                      <Box sx={{ display: "flex", gap: "4px" }}>
                        {action.shortcut.map((key, index) => (
                          <Box
                            key={index}
                            component="kbd"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: "20px",
                              height: "20px",
                              padding: "0 5px",
                              fontSize: "10px",
                              fontWeight: 600,
                              color: "rgba(255, 255, 255, 0.7)",
                              background: "rgba(255, 255, 255, 0.08)",
                              border: "1px solid rgba(255, 255, 255, 0.15)",
                              borderRadius: "4px",
                              textTransform: "uppercase",
                              fontFamily: "monospace",
                            }}
                          >
                            {key}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Command.Item>
                ))}
              </Command.Group>
            ))}
          </Command.List>
        </Command>
      </Box>
    </>
  );
}

export default CommandBar;
